import React from 'react';
import { Button, Stack, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadMultipleIcon from '@mui/icons-material/FileDownload';
import GridOnIcon from '@mui/icons-material/GridOn';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { saveAs } from 'file-saver';
import { usePdf } from '../../context/PdfContext';
import { xlsxToCsv, xlsxToJson } from '../../utils/spreadsheetUtils';

/** Strip a known document extension from a filename. */
const stripExtension = (name: string): string =>
  name.replace(/\.(pdf|docx|xlsx)$/i, '');

const DownloadButton: React.FC = () => {
  const {
    files,
    currentFileId,
    getSelectedFilesText,
    getSelectedFilesMarkdown,
    outputFormat,
    ensureMarkdown,
  } = usePdf();

  const isMarkdown = outputFormat === 'markdown';
  const currentFile = files.find((file) => file.id === currentFileId);
  const selectedFiles = files.filter(file => file.selected && file.text);
  const hasSelectedFiles = selectedFiles.length > 0;

  const handleDownload = async () => {
    if (!currentFile) return;

    let content = currentFile.text;
    let extension = 'txt';
    let mime = 'text/plain;charset=utf-8';

    if (isMarkdown) {
      content = currentFile.markdown || (await ensureMarkdown(currentFile.id));
      extension = 'md';
      mime = 'text/markdown;charset=utf-8';
    }

    if (!content) return;

    const blob = new Blob([content], { type: mime });
    const fileName = `${stripExtension(currentFile.file.name)}.${extension}`;
    saveAs(blob, fileName);
  };

  const handleBatchDownload = () => {
    if (!hasSelectedFiles) return;

    const content = isMarkdown ? getSelectedFilesMarkdown() : getSelectedFilesText();
    if (!content) return;

    const extension = isMarkdown ? 'md' : 'txt';
    const mime = isMarkdown ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8';

    const blob = new Blob([content], { type: mime });
    const fileName = `documents-batch-${new Date().toISOString().slice(0, 10)}.${extension}`;
    saveAs(blob, fileName);
  };

  const isXlsx = currentFile?.fileType === 'xlsx';

  const handleSpreadsheetDownload = async (kind: 'csv' | 'json') => {
    if (!currentFile) return;
    const content = kind === 'csv'
      ? await xlsxToCsv(currentFile.file)
      : await xlsxToJson(currentFile.file);
    const mime = kind === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8';
    const blob = new Blob([content], { type: mime });
    saveAs(blob, `${stripExtension(currentFile.file.name)}.${kind}`);
  };

  const currentDisabled =
    !currentFile ||
    currentFile.isProcessing ||
    (isMarkdown ? false : !currentFile.text);

  const downloadLabel = isMarkdown ? 'Download as .md' : 'Download Current File';

  return (
    <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        disabled={currentDisabled}
      >
        {downloadLabel}
      </Button>

      {isXlsx && (
        <>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GridOnIcon />}
            onClick={() => handleSpreadsheetDownload('csv')}
            disabled={!currentFile || currentFile.isProcessing}
          >
            Download .csv
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DataObjectIcon />}
            onClick={() => handleSpreadsheetDownload('json')}
            disabled={!currentFile || currentFile.isProcessing}
          >
            Download .json
          </Button>
        </>
      )}

      <Tooltip title={hasSelectedFiles ? `Download ${selectedFiles.length} selected files` : 'Select files to download'}>
        <span>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileDownloadMultipleIcon />}
            onClick={handleBatchDownload}
            disabled={!hasSelectedFiles}
          >
            {isMarkdown ? 'Download Selected (.md)' : 'Download Selected'}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default DownloadButton;
